import { call, put, takeEvery, select, all } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import {
  getOrganizationsList,
  updateOrganization,
  getRules,
  addRule,
  removeRule,
  addUser,
  removeUser,
  getOrganizationsCount,
  getAnalysts,
  addDefaultAttendee,
  updateDefaultAttendee,
  removeAttendee,
  validateOrganization,
} from '@services/organizationsResource';
import { tokenSelector } from '@selectors/authentication';
import { types } from '@actions/organizations';
import {
  selectOrganizationsEditForm,
  selectOrganizationsEditRules,
  selectOrganizationsEditUsers,
  selectOrganizationsList,
  selectOrganizationsFilter,
  organizationEditAttendees,
  selectOrganizationsRules,
} from '@selectors/organizations';
import { assignIntroMessageToOrganization } from '@actions/introMessages';
import { ERROR_MESSAGES } from '@config/api-messages';
import _ from 'lodash';

const updateRules = function*({ id, token }) {
  const newRules = yield select(selectOrganizationsEditRules);
  const oldOrganizationRules = (yield select(selectOrganizationsList)).find(
    (org) => org.id === id,
  ).rules;

  const oldRules = oldOrganizationRules.map((item) => item.id);
  yield all(
    newRules.map((ruleId) =>
      oldRules.indexOf(ruleId) === -1
        ? call(addRule, {
            token,
            orgId: id,
            ruleId,
          })
        : ruleId,
    ),
  );

  yield all(
    oldRules.map((ruleId) =>
      newRules.indexOf(ruleId) === -1
        ? call(removeRule, {
            token,
            orgId: id,
            ruleId,
          })
        : ruleId,
    ),
  );
};

const updateUsers = function*({ id: orgId, token }) {
  const newUsers = (yield select(selectOrganizationsEditUsers)).map(({ id }) => id);
  const oldOrganizationUsers = (yield select(selectOrganizationsList)).find(
    (org) => org.id === orgId,
  ).users;

  const oldUsers = oldOrganizationUsers.map((item) => item.id);

  const addUsersList = _.differenceWith(newUsers, oldUsers, _.isEqual);
  const removeUsersList = _.differenceWith(oldUsers, newUsers, _.isEqual);

  addUsersList.length &&
    (yield all(
      addUsersList.map((userId) =>
        call(addUser, {
          token,
          orgId,
          userId,
        }),
      ),
    ));

  removeUsersList.length &&
    (yield all(
      removeUsersList.map((userId) =>
        call(removeUser, {
          token,
          orgId,
          userId,
        }),
      ),
    ));
};

const addUsersToNewOrg = function*({ id: orgId, token }) {
  const newUsers = (yield select(selectOrganizationsEditUsers)).map(({ id }) => id);

  newUsers.length &&
    (yield all(
      newUsers.map((userId) =>
        call(addUser, {
          token,
          orgId,
          userId,
        }),
      ),
    ));
};

const setRulesToNewOrg = function*({ id, token }) {
  const newRules = yield select(selectOrganizationsEditRules);
  yield all(
    newRules.map((ruleId) =>
      call(addRule, {
        token,
        orgId: id,
        ruleId,
      }),
    ),
  );
};

const addDefaultAttendeesToNewOrganization = function*({ id: organizationId, token }) {
  try {
    const attendees = yield select(organizationEditAttendees);
    if (!attendees.length) return;
    yield all(
      attendees.map((attendee) =>
        call(addDefaultAttendee, { token, organizationId, attendee }),
      ),
    );
  } catch (err) {
    throw err;
  }
};

const updateDefaultAttendees = function*({ id: organizationId, token }) {
  try {
    const attendeesFromEditForm = yield select(organizationEditAttendees);
    const attendeesOriginal = (yield select(selectOrganizationsList)).find(
      (organization) => organization.id === organizationId,
    ).attendees;
    const getId = ({ id }) => id;
    const updatedAttendees = _.differenceWith(
      attendeesFromEditForm,
      attendeesOriginal,
      _.isEqual,
    ).filter(getId);
    const newAttendees = attendeesFromEditForm.filter(({ id }) => !id);
    const removedAttendees = _.differenceBy(
      attendeesOriginal,
      attendeesFromEditForm,
      'id',
    );
    yield all(
      removedAttendees.map(({ id: attendeeId }) =>
        call(removeAttendee, { token, organizationId, attendeeId }),
      ),
    );
    yield all(
      updatedAttendees
        .filter(getId)
        .map((attendee) =>
          call(updateDefaultAttendee, { token, organizationId, attendee }),
        ),
    );
    yield all(
      newAttendees.map((attendee) =>
        call(addDefaultAttendee, { token, organizationId, attendee }),
      ),
    );
  } catch (err) {
    throw err;
  }
};

export function* createOrganizationSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const organizationData = yield select(selectOrganizationsEditForm);
    const rulesList = yield select(selectOrganizationsRules);
    validateOrganization(organizationData, rulesList);
    const response = (yield call(updateOrganization, {
      token,
      ...organizationData,
    })).data;
    const { id } = response;

    yield setRulesToNewOrg({ id, token });
    yield addUsersToNewOrg({ id, token });
    yield addDefaultAttendeesToNewOrganization({ id, token });
    yield put(
      assignIntroMessageToOrganization({
        token,
        id: organizationData.introMessageId,
        organizationId: id,
      }),
    );
    yield put({
      type: types.CREATE_ORGANIZATION.SUCCESS,
      message: 'Organization successfully created',
    });
    yield put(push('/organizations'));
  } catch (err) {
    const originalMessage = _.get(err, 'response.data.error.message');
    const apiErrorMessage =
      originalMessage === ERROR_MESSAGES.ORGANIZATION_NAME_FIELD_API_ERROR
        ? ERROR_MESSAGES.ORGANIZATION_NAME_FIELD_USER_ERROR
        : originalMessage;

    yield put({
      type: types.CREATE_ORGANIZATION.FAILURE,
      message: apiErrorMessage || err.message || 'Error while creating an organization.',
    });
  }
}

export function* updateOrganizationSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const organizationData = yield select(selectOrganizationsEditForm);
    const rulesList = yield select(selectOrganizationsRules);
    validateOrganization(organizationData, rulesList);
    const response = (yield call(updateOrganization, {
      token,
      ...organizationData,
    })).data;

    yield updateRules({ id: organizationData.id, token });
    yield updateUsers({ id: organizationData.id, token });
    yield updateDefaultAttendees({ id: organizationData.id, token });
    yield put(
      assignIntroMessageToOrganization({
        token,
        id: organizationData.introMessageId,
        organizationId: organizationData.id,
      }),
    );

    yield put(push('/organizations'));
  } catch (err) {
    yield put({
      type: types.UPDATE_ORGANIZATION.FAILURE,
      message:
        _.get(err, 'response.data.error.message') ||
        'Error while updating an organization.',
    });
  }
}

export function* getOrganizationsListSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const { limit, currentPage, name } = yield select(selectOrganizationsFilter);
    const filter = {
      where: {
        name: {
          like: `%${name}%`,
        },
      },
      limit,
    };
    const page = { page: name ? 1 : currentPage };
    const response = (yield call(getOrganizationsList, { token, filter, page })).data;
    yield put({
      type: types.LOAD_ORGANIZATIONS.SUCCESS,
      payload: { ...response },
    });
  } catch (err) {
    yield put({
      type: types.LOAD_ORGANIZATIONS.FAILURE,
      message:
        _.get(err, 'response.data.error.message') || 'Error while loading organizations.',
    });
  }
}

export function* loadOrganizationsRulesSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const { data } = yield call(getRules, {
      token,
    });

    yield put({
      type: types.LOAD_RULES.SUCCESS,
      payload: [...data],
    });
  } catch (err) {
    yield put({
      type: types.LOAD_RULES.FAILURE,
      message: _.get(err, 'response.data.error.message') || 'Failed to load rules list.',
    });
  }
}

export function* changePageSaga(action) {
  try {
    yield put({
      type: types.FILTER.SET,
      payload: action.payload,
    });

    yield put({
      type: types.LOAD_ORGANIZATIONS.REQUEST,
    });
  } catch (e) {
    console.log('changePageSaga error', e);
  }
}

export function* getAllOrganizationsListSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const { count } = (yield call(getOrganizationsCount, { token })).data;
    yield put({
      type: types.FILTER.SET,
      payload: { limit: count },
    });
    yield put({
      type: types.LOAD_ORGANIZATIONS.REQUEST,
    });
    yield put({
      type: types.FILTER.CLEAR,
    });
  } catch (err) {
    yield put({
      type: types.LOAD_ALL_ORGANIZATIONS.FAILURE,
      message:
        _.get(err, 'response.data.error.message') || 'Failed to load organizations list.',
    });
  }
}

export function* loadAnalyticsListSaga(action) {
  try {
    const token = yield select(tokenSelector);
    const keyWord = action.payload;
    const analyticsResponse = (yield call(getAnalysts, { token, keyWord })).data.users;

    yield put({
      type: types.LOAD_ANALYSTS_LIST.SUCCESS,
      payload: analyticsResponse,
    });
  } catch (e) {
    yield put({
      type: types.LOAD_ANALYSTS_LIST.FAILURE,
      message:
        _.get(err, 'response.data.error.message') || 'Failed to load list of analytics.',
    });
  }
}

export default function*() {
  yield takeEvery(types.LOAD_ORGANIZATIONS.REQUEST, getOrganizationsListSaga);
  yield takeEvery(types.UPDATE_ORGANIZATION.REQUEST, updateOrganizationSaga);
  yield takeEvery(types.CREATE_ORGANIZATION.REQUEST, createOrganizationSaga);
  yield takeEvery(types.LOAD_ANALYSTS_LIST.REQUEST, loadAnalyticsListSaga);
  yield takeEvery(types.LOAD_RULES.REQUEST, loadOrganizationsRulesSaga);
  yield takeEvery(types.CHANGE_PAGE, changePageSaga);
  yield takeEvery(types.LOAD_ALL_ORGANIZATIONS.REQUEST, getAllOrganizationsListSaga);
}



// WEBPACK FOOTER //
// ./redux/sagas/organizations.js